/**
 * TeamDetail Page
 * Full management page for a single team.
 * - View all team members with their roles
 * - Edit member roles (checkboxes)
 * - Toggle "owner" role per member
 * - Add new members by email
 * - Remove members
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTeam,
  listTeamMemberships,
  updateMembershipRoles,
  addTeamMember,
  removeTeamMember,
  getTeamPrefs,
  ADMINS_TEAM_ID,
} from '../../appwrite';
import './TeamDetail.css';

// Default roles always available
const DEFAULT_ROLES = ['admin', 'manager', 'founder', 'salesOnly', 'production'];

const TeamDetail = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null); // Team object
  const [members, setMembers] = useState([]); // Team memberships
  const [customRoles, setCustomRoles] = useState([]); // Custom roles from admins prefs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Edit roles state
  const [editingMember, setEditingMember] = useState(null); // Membership ID being edited
  const [editRoles, setEditRoles] = useState([]); // Temp roles during edit
  const [savingMember, setSavingMember] = useState(null); // Membership ID being saved

  // Add member modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addRoles, setAddRoles] = useState([]);
  const [addingMember, setAddingMember] = useState(false);

  // Remove confirmation
  const [removeConfirm, setRemoveConfirm] = useState(null);

  // All available roles: defaults + custom
  const allRoles = [...DEFAULT_ROLES, ...customRoles];

  /**
   * Fetches team info, members, and custom roles.
   */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch team info and members in parallel
      const [teamData, membersData] = await Promise.all([
        getTeam(teamId),
        listTeamMemberships(teamId, { limit: 100 }),
      ]);
      setTeam(teamData);
      setMembers(membersData.memberships);

      // Fetch custom roles from admins team prefs
      try {
        const prefs = await getTeamPrefs(ADMINS_TEAM_ID);
        if (prefs.customRoles && Array.isArray(prefs.customRoles)) {
          setCustomRoles(prefs.customRoles);
        }
      } catch (e) {
        // No custom roles
      }
    } catch (err) {
      setError(err.message || 'Failed to load team.');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ===== Role Editing =====

  const startEditing = (member) => {
    setEditingMember(member.$id);
    setEditRoles(member.roles.filter(r => r !== 'owner'));
  };

  const cancelEditing = () => {
    setEditingMember(null);
    setEditRoles([]);
  };

  const toggleRole = (role) => {
    setEditRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  /** Saves updated roles for a member (preserves owner if they have it). */
  const saveRoles = async (member) => {
    setSavingMember(member.$id);
    try {
      const hasOwner = member.roles.includes('owner');
      const finalRoles = hasOwner ? [...editRoles, 'owner'] : editRoles;
      await updateMembershipRoles(teamId, member.$id, finalRoles);
      setEditingMember(null);
      setEditRoles([]);
      showSuccess(`Roles updated for ${member.userName || member.userEmail}`);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to update roles.');
    } finally {
      setSavingMember(null);
    }
  };

  // ===== Owner Toggle =====

  const toggleOwner = async (member) => {
    setSavingMember(member.$id);
    try {
      const hasOwner = member.roles.includes('owner');
      const newRoles = hasOwner
        ? member.roles.filter(r => r !== 'owner')
        : [...member.roles, 'owner'];
      await updateMembershipRoles(teamId, member.$id, newRoles);
      showSuccess(`Owner ${hasOwner ? 'removed from' : 'granted to'} ${member.userName || member.userEmail}`);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to update owner role.');
    } finally {
      setSavingMember(null);
    }
  };

  // ===== Add Member =====

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setAddingMember(true);
    setError('');
    try {
      await addTeamMember(teamId, addEmail.trim(), addRoles);
      setShowAddModal(false);
      setAddEmail('');
      setAddRoles([]);
      showSuccess('Member added successfully.');
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to add member.');
    } finally {
      setAddingMember(false);
    }
  };

  const toggleAddRole = (role) => {
    setAddRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  // ===== Remove Member =====

  const handleRemoveMember = async (membershipId) => {
    setSavingMember(membershipId);
    setRemoveConfirm(null);
    try {
      await removeTeamMember(teamId, membershipId);
      showSuccess('Member removed.');
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to remove member.');
    } finally {
      setSavingMember(null);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Loading state
  if (loading) {
    return (
      <div className="team-detail-page">
        <div className="members-loading">
          <div className="loading-spinner"></div>
          <p>Loading team...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-detail-page">
      {/* Back button + Team header */}
      <div className="team-detail-header">
        <button className="back-btn" onClick={() => navigate('/teams')}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M11 4L6 9L11 14" />
          </svg>
          Back to Teams
        </button>
        <div className="team-detail-title-row">
          <div>
            <h1 className="page-title">{team?.name || 'Team'}</h1>
            <p className="team-detail-subtitle">
              {members.length} member{members.length !== 1 ? 's' : ''} · Created {team && new Date(team.$createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <button className="create-btn" onClick={() => setShowAddModal(true)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3V13M3 8H13" />
            </svg>
            Add Member
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="members-alert members-alert-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMessage && (
        <div className="members-alert members-alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      {/* Members table */}
      <div className="members-table-container">
        <table className="members-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Roles</th>
              <th>Owner</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map(member => (
              <tr key={member.$id} className={savingMember === member.$id ? 'saving' : ''}>
                {/* Member info */}
                <td>
                  <div className="member-info">
                    <div className="member-avatar">
                      {(member.userName || member.userEmail)?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="member-details">
                      <span className="member-name">{member.userName || 'Unnamed'}</span>
                      <span className="member-email">{member.userEmail}</span>
                    </div>
                  </div>
                </td>

                {/* Roles */}
                <td>
                  {editingMember === member.$id ? (
                    <div className="role-editor">
                      {allRoles.map(role => (
                        <label key={role} className="role-checkbox">
                          <input
                            type="checkbox"
                            checked={editRoles.includes(role)}
                            onChange={() => toggleRole(role)}
                          />
                          <span className="role-checkbox-label">{role}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="role-badges">
                      {member.roles
                        .filter(r => r !== 'owner')
                        .map(role => (
                          <span key={role} className={`role-badge ${DEFAULT_ROLES.includes(role) ? 'role-default' : 'role-custom'}`}>
                            {role}
                          </span>
                        ))}
                      {member.roles.filter(r => r !== 'owner').length === 0 && (
                        <span className="role-badge role-none">No roles</span>
                      )}
                    </div>
                  )}
                </td>

                {/* Owner toggle */}
                <td>
                  <button
                    className={`owner-toggle ${member.roles.includes('owner') ? 'is-owner' : ''}`}
                    onClick={() => toggleOwner(member)}
                    disabled={savingMember === member.$id}
                    title={member.roles.includes('owner') ? 'Remove owner' : 'Make owner'}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 1.5L9.85 5.75L14.5 6.15L11 9.25L12.1 13.85L8 11.5L3.9 13.85L5 9.25L1.5 6.15L6.15 5.75L8 1.5Z" />
                    </svg>
                  </button>
                </td>

                {/* Actions */}
                <td>
                  {editingMember === member.$id ? (
                    <div className="member-actions">
                      <button className="action-btn action-save" onClick={() => saveRoles(member)} disabled={savingMember === member.$id}>
                        {savingMember === member.$id ? '...' : 'Save'}
                      </button>
                      <button className="action-btn action-cancel" onClick={cancelEditing}>Cancel</button>
                    </div>
                  ) : (
                    <div className="member-actions">
                      <button className="action-btn action-edit" onClick={() => startEditing(member)}>
                        Edit Roles
                      </button>
                      {removeConfirm === member.$id ? (
                        <>
                          <button className="action-btn action-delete-confirm" onClick={() => handleRemoveMember(member.$id)} disabled={savingMember === member.$id}>
                            Confirm
                          </button>
                          <button className="action-btn action-cancel" onClick={() => setRemoveConfirm(null)}>Cancel</button>
                        </>
                      ) : (
                        <button className="action-btn action-delete" onClick={() => setRemoveConfirm(member.$id)}>
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {members.length === 0 && (
          <div className="members-empty">
            <p>No members in this team. Add someone to get started.</p>
          </div>
        )}
      </div>

      {/* ===== Add Member Modal ===== */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Add Member</h2>
            <form className="modal-form" onSubmit={handleAddMember}>
              <div className="modal-field">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="user@example.com"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="modal-field">
                <label>Roles (optional)</label>
                <div className="role-editor">
                  {allRoles.map(role => (
                    <label key={role} className="role-checkbox">
                      <input
                        type="checkbox"
                        checked={addRoles.includes(role)}
                        onChange={() => toggleAddRole(role)}
                      />
                      <span className="role-checkbox-label">{role}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="action-btn action-cancel" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-save" disabled={addingMember}>
                  {addingMember ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetail;
